import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $cardsListStore, $initialAccountsStore, $profileFeatureStore, $usersStore } from '@lib/store';
import { AccountUtils, ValidateUtils } from '@shared/utils';
import { AccountType, UsersRequest, UserType } from '@shared/api';
import { SelectUserAccountsAndCards } from './selectUserAccountsAndCards';
import { BaseInput, Button, ErrorMessage, Modal, PhoneInput, Radio } from '@shared/widgets';
import { Constants } from '@shared/constants';

const USER_TYPE_OPTIONS = [
  { value: UserType.MANAGER, text: 'modals.add.userTypes.manager' },
  { value: UserType.USER, text: 'modals.add.userTypes.user' },
];

interface IForm {
  userName: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  position: string;
  phone: { countryCode: string; phoneNumber: string };
  userType: UserType;
  accounts: number[];
  cards: number[];
}
const DEFAULT_VALUES: IForm = {
  userName: '',
  firstName: '',
  lastName: '',
  emailAddress: '',
  position: '',
  phone: { countryCode: Constants.COUNTRY_CODE, phoneNumber: '' },
  userType: UserType.USER,
  accounts: [],
  cards: [],
};

interface Props {
  show: boolean;
  onClose: () => void;
  params: UsersRequest;
}

export const AddNewUserModal = (props: Props) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const userAccountsStore = useUnit($initialAccountsStore.store);
  const userCardsStore = useUnit($cardsListStore.store);

  const [selectedAccount, setSelectedAccount] = useState<number | undefined>();

  const { control, formState, handleSubmit, watch, setValue, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const userType = watch('userType');
  const { t } = useTranslation('userManagement');

  useEffect(() => {
    if (!selectedAccount) return;
    $cardsListStore.events.getUserCards({ AccountId: selectedAccount, Count: 100 });
    return () => $cardsListStore.events.clear();
  }, [selectedAccount]);

  const closeHandler = () => {
    props.onClose();
    reset(DEFAULT_VALUES);
  };
  const submitHandler = async (data: IForm) => {
    await $usersStore.events.addUser({
      user: {
        userName: data.userName,
        firstName: data.firstName,
        lastName: data.lastName,
        emailAddress: data.emailAddress,
        position: data.position,
        phoneAreaCode: data.phone.countryCode,
        phoneNumber: data.phone.phoneNumber,
        userType: data.userType,
        accountId: data.accounts,
        cardId: data.cards,
      },
      params: props.params,
    }),
      closeHandler();
  };

  const accountList = useMemo(() => {
    if (userAccountsStore.loading || !profileFeatureStore.data?.accountFlows) return [];
    return AccountUtils.filterAccounts(profileFeatureStore.data.accountFlows, userAccountsStore.data);
  }, [userAccountsStore.loading, profileFeatureStore.data?.accountFlows]);
  const accountCardsList = useMemo(
    () => (accountList.length > 0 ? accountList.filter((account) => account.type === AccountType.Card) : []),
    [accountList],
  );
  return (
    <Modal isOpen={props.show} onClose={closeHandler} title={t('modals.add.title')}>
      <form className='flex flex-col gap-5' noValidate onSubmit={handleSubmit(submitHandler)}>
        <Controller
          name='userName'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            minLength: { value: 8, message: t('modals.add.minimumError') },
            pattern: { value: ValidateUtils.noSpacesRegex, message: t('errors.form.noSpaces', { ns: 'common' }) },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} placeholder={t('placeholders.username', { ns: 'common' })} required />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='firstName'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} placeholder={t('placeholders.firstName', { ns: 'common' })} required />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='lastName'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} placeholder={t('placeholders.lastName', { ns: 'common' })} required />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='emailAddress'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            pattern: { value: ValidateUtils.emailRegex, message: t('errors.form.email.invalidFormat', { ns: 'common' }) },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} type='email' placeholder={t('placeholders.email', { ns: 'common' })} required />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='position'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} placeholder={t('placeholders.positionRole', { ns: 'common' })} required />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='phone'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            validate: (value) => {
              if (!value?.countryCode) return t('errors.form.required', { ns: 'common' });
              if (!value?.phoneNumber) return t('errors.form.required', { ns: 'common' });
              if (!ValidateUtils.validatePhoneNumber(value.phoneNumber)) return t('errors.form.phone.invalidFormat', { ns: 'common' });
              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <PhoneInput enableSearch required value={field.value} onChange={field.onChange} />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='userType'
          control={control}
          render={({ field }) => (
            <Radio
              orientation='horizontal'
              options={USER_TYPE_OPTIONS.map((option) => ({ ...option, text: t(option.text) }))}
              value={field.value}
              onChange={(e) => {
                field.onChange(e);
                if (userType === UserType.USER) {
                  setSelectedAccount(undefined);
                  setValue('cards', []);
                } else setValue('accounts', []);
              }}
            />
          )}
        />
        {userType === UserType.MANAGER && (
          <Controller
            name='accounts'
            control={control}
            rules={{ required: t('errors.form.required', { ns: 'common' }) }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <SelectUserAccountsAndCards
                  userType={userType}
                  accountLoading={userAccountsStore.loading}
                  accountList={accountList}
                  selectedAccounts={field.value}
                  changeAccountOrCardId={field.onChange}
                />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        {userType === UserType.USER && (
          <Controller
            name='cards'
            control={control}
            rules={{ required: t('errors.form.required', { ns: 'common' }) }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <SelectUserAccountsAndCards
                  userType={userType}
                  accountLoading={userAccountsStore.loading}
                  accountList={accountCardsList}
                  selectedAccount={selectedAccount}
                  setSelectedAccount={setSelectedAccount}
                  selectedCardValue={selectedAccount}
                  cardList={userCardsStore.data}
                  cardLoading={userCardsStore.loading}
                  selectedCards={field.value}
                  changeAccountOrCardId={field.onChange}
                />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        <Button className='mt-7' fullWidth color='primary' type='submit' disabled={formState.isSubmitting}>
          {t('buttons.saveChanges', { ns: 'common' })}
        </Button>
      </form>
    </Modal>
  );
};
